import { OutOfBandTransactionStatus } from "@/modules/finances/boxPaymentAtCounter/entities/outOfBandTransactionType"
import { StripeErrorCode } from "@/modules/finances/types/stripeErrorTypes"
import { AddressDtoSchema } from "@modules/address/addressDto"
import { PhoneNumberWithPrefixSchema } from "@modules/box/boxTypes"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { HUImageDtoSchema } from "@modules/dtos/imageDto"
import {
  DisputeReason,
  DisputeStatus,
  PaymentMethodType,
  TransactionStatus,
} from "@modules/finances/types/financesType"
import { z } from "zod"

export const TransactionCouponDetailsDtoSchema = z
  .object({
    amount: z.number(),
    currency: z.string(),
    type: z.enum(CouponType),
  })
  .meta({ id: "TransactionCouponDetailsDtoSchema" })
export type TransactionCouponDetailsDto = z.infer<typeof TransactionCouponDetailsDtoSchema>

export const TransactionPromoCodeDetailsDtoSchema = z
  .object({
    amount: z.number(),
    currency: z.string(),
    type: z.enum(CouponType),
    name: z.string().nullable(),
  })
  .meta({ id: "TransactionPromoCodeDetailsDtoSchema" })
export type TransactionPromoCodeDetailsDto = z.infer<typeof TransactionPromoCodeDetailsDtoSchema>

export const TransactionUserDtoSchema = z
  .object({
    id: z.string().nullable(),
    firstname: z.string().nullable(),
    lastname: z.string().nullable(),
    username: z.string().nullable(),
    avatar: HUImageDtoSchema.nullable(),
    isAnonymous: z.boolean(),
  })
  .meta({ id: "TransactionUserDtoSchema" })

export const TransactionUserDetailsDtoSchema = z
  .object({
    id: z.string(),
    boxMemberNumber: z.string().nullable(),
    firstname: z.string(),
    lastname: z.string(),
    username: z.string(),
    avatar: HUImageDtoSchema.nullable(),
    address: AddressDtoSchema.nullable(),
    email: z.string(),
    nationality: z.string().nullable(),
    phoneNumber: PhoneNumberWithPrefixSchema.nullable(),
    stripeCustomerId: z.string(),
  })
  .meta({ id: "TransactionUserDetailsDtoSchema" })

export const TransactionBoxDetailsDtoSchema = z
  .object({
    id: z.string(),
    stripeAccountId: z.string(),
    name: z.string(),
    logo: HUImageDtoSchema,
    email: z.string(),
    address: AddressDtoSchema.nullable(),
    phoneNumber: PhoneNumberWithPrefixSchema.nullable(),
    nationality: z.string().nullable(),
  })
  .meta({ id: "TransactionBoxDetailsDtoSchema" })

export enum TransactionProductType {
  PROGRAM_VIRTUAL = "PROGRAM_VIRTUAL",
  PROGRAM_ON_OFF = "PROGRAM_ON_OFF",
  PROGRAM_PRO = "PROGRAM_PRO",
  PROGRAM_BUNDLE_VIRTUAL = "PROGRAM_BUNDLE_VIRTUAL",
  PROGRAM_BUNDLE_ON_OFF = "PROGRAM_BUNDLE_ON_OFF",
  BOX_OFFER_SUBSCRIPTION = "BOX_OFFER_SUBSCRIPTION",
  BOX_OFFER_TRIAL = "BOX_OFFER_TRIAL",
  BOX_OFFER_TICKETS_BOOK = "BOX_OFFER_TICKETS_BOOK",
  BOX_OFFER_DROP_IN = "BOX_OFFER_DROP_IN",
  BOX_OFFER_MILLENIUM = "BOX_OFFER_MILLENIUM",
  BOX_SALE = "BOX_SALE",
}

export const TransactionProductDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    type: z.enum(TransactionProductType),
  })
  .meta({ id: "TransactionProductDtoSchema" })

export const TransactionPayoutDetailDtoSchema = z
  .object({
    payoutId: z.string(),
    payoutAt: z.iso.datetime(),
  })
  .meta({ id: "TransactionPayoutDetailDtoSchema" })

export const LightPaymentErrorDtoSchema = z
  .object({
    errorCode: z.enum(StripeErrorCode),
    declineCode: z.enum(StripeErrorCode).nullable(),
    date: z.iso.datetime(),
  })
  .meta({ id: "LightPaymentErrorDtoSchema" })

export const TransactionDtoSchema = z
  .object({
    transactionId: z.string(),
    date: z.iso.datetime(),
    paidAt: z.iso.datetime().nullable(),
    user: TransactionUserDtoSchema,
    amount: z.number(),
    fee: z.number(),
    netAmount: z.number(), // netAmount = Amout - fee
    currency: z.string(),
    status: z.enum(TransactionStatus),
    disputed: z.boolean(),
    refunded: z.boolean(),
    product: TransactionProductDtoSchema,
    paymentMethodType: z.enum(PaymentMethodType),
    outOfBandStatus: z.enum(OutOfBandTransactionStatus).nullable(),
    payoutAt: z.iso.datetime().nullable(),
    paymentError: LightPaymentErrorDtoSchema.nullable(),
  })
  .meta({ id: "TransactionDtoSchema" })

export enum DiscountType {
  DISCOUNT = "DISCOUNT",
  PROMO_CODE = "PROMO_CODE",
}

export const TransactionLineDtoSchema = z
  .object({
    description: z.string().nullable(),
    netAmount: z.number(),
    taxAmount: z.number().nullable(),
    totalAmount: z.number(), // totalAmount = netAmount - taxAmount
    taxPercent: z.number().nullable(),
    quantity: z.number(),
    currency: z.string(),
    periodStart: z.string().nullable(),
    periodEnd: z.string().nullable(),
  })
  .meta({ id: "TransactionLineDtoSchema" })

export const TransactionDiscountDtoSchema = z
  .object({
    amount: z.number(),
    currency: z.string().nullable(), // Null uniquement si CouponType == PERCENT
    couponType: z.enum(CouponType),
    name: z.string().nullable(),
    type: z.enum(DiscountType),
  })
  .meta({ id: "TransactionDiscountDtoSchema" })

export const TransactionDetailHistoryDtoSchema = z
  .object({
    date: z.iso.datetime(),
    status: z.enum(TransactionStatus),
    errorCode: z.enum(StripeErrorCode).nullable(), // The declined card issue when errorCode is CARD_DECLINED
  })
  .meta({ id: "TransactionDetailHistoryDtoSchema" })

export const TransactionDetailsDtoSchema = z
  .object({
    transactionId: z.string(),
    date: z.iso.datetime(),
    paidAt: z.iso.datetime().nullable(),
    user: TransactionUserDetailsDtoSchema.nullable(),
    isAnonymous: z.boolean(), // if current client is anonymous
    box: TransactionBoxDetailsDtoSchema.nullable(),
    amount: z.number(), // Total amount including taxes and fees
    netAmount: z.number(), // Amount excluding taxes and fees -> netAmount = Amout - fee
    refundAmount: z.number(), // Amount refunded
    totalTaxAmount: z.number(), // Total tax amount applied
    fee: z.number(), // Total fees applied
    currency: z.string(),
    status: z.enum(TransactionStatus),
    disputed: z.boolean(), // Indicates if the transaction is disputed (chargeback)
    refundable: z.boolean(), // Indicates if the transaction is refundable
    refunded: z.boolean(), // Indicates if the transaction is refunded
    invoiceNumber: z.string(), // Stripe invoice number
    invoiceId: z.string().nullable(),
    invoicePdfUrl: z.string().nullable(), // URL of the Stripe invoice PD
    lines: z.array(TransactionLineDtoSchema), // Transaction lines (currently, each transaction has only one line)
    discounts: z.array(TransactionDiscountDtoSchema), // Applied discounts, such as promo code and/or coupon subscription
    paymentMethodDetail: z
      .object({
        type: z.enum(PaymentMethodType),
        last4Digits: z.string().nullable(),
      })
      .nullable(),
    disputeDetail: z
      .object({
        amount: z.number(), // Total amount of dispute
        fee: z.number(), // Total fees amount of dispute
        status: z.enum(DisputeStatus), // Current status of the dispute
        reason: z.enum(DisputeReason), // Reason of the dispute
      })
      .nullable(), // Dispute information, only if disputed == true
    history: z.array(TransactionDetailHistoryDtoSchema).nullable(),
    outOfBandStatus: z.enum(OutOfBandTransactionStatus).nullable(),
    payout: TransactionPayoutDetailDtoSchema.nullable(),
  })
  .meta({ id: "TransactionDetailsDtoSchema" })

export type TransactionDto = z.infer<typeof TransactionDtoSchema>
export type TransactionDetailsDto = z.infer<typeof TransactionDetailsDtoSchema>
export type TransactionLineDto = z.infer<typeof TransactionLineDtoSchema>
export type TransactionDiscountDto = z.infer<typeof TransactionDiscountDtoSchema>
export type TransactionProductDto = z.infer<typeof TransactionProductDtoSchema>
export type TransactionUserDto = z.infer<typeof TransactionUserDtoSchema>
export type TransactionUserDetailsDto = z.infer<typeof TransactionUserDetailsDtoSchema>
export type TransactionBoxDetailsDto = z.infer<typeof TransactionBoxDetailsDtoSchema>
export type TransactionDetailHistoryDto = z.infer<typeof TransactionDetailHistoryDtoSchema>
export type TransactionPayoutDetailDto = z.infer<typeof TransactionPayoutDetailDtoSchema>
export type LightPaymentErrorDto = z.infer<typeof LightPaymentErrorDtoSchema>
